import { Component } from 'react';
import styled from 'styled-components';

// Top-level safety net. React unmounts the ENTIRE tree on any uncaught render
// error, which the user sees as a blank page — the app otherwise has no error
// boundary. This catches render errors below <Router /> and shows a minimal
// system-error screen with a Close action instead of a blank page.
//
// It is deliberately self-contained: it does NOT use app hooks/providers, the
// i18n layer, or the portal-modal system, because those live inside the subtree
// that just crashed and could crash the fallback too. Copy is mocked pending
// i18n keys; Close reloads because a render crash usually leaves state
// unrecoverable (kept in one method so a softer recovery can be swapped in).
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('🔥 ErrorBoundary caught a render error ↩', error, info);
  }

  handleClose = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <Overlay role="alertdialog" aria-modal="true">
        <Card>
          {/* TODO(i18n): replace mocked copy with translation keys. */}
          <Title>Something went wrong</Title>
          <Content>
            The app ran into an unexpected error. Please close this message and
            try again.
          </Content>
          <CloseButton type="button" onClick={this.handleClose}>
            Close
          </CloseButton>
        </Card>
      </Overlay>
    );
  }
}

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(33, 33, 33, 0.7);
`;
const Card = styled.div`
  width: 100%;
  max-width: 343px;
  padding: 24px 16px;
  border-radius: 8px;
  background: #fff;
  text-align: center;
`;
const Title = styled.p`
  margin: 0 0 8px;
  font-family: var(--ff-primary);
  font-weight: 700;
  font-size: 16px;
  line-height: 20px;
  color: var(--ds-c-blue);
`;
const Content = styled.p`
  margin: 0 0 24px;
  font-family: var(--ff-primary);
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;
  color: var(--ds-c-grey-dark);
`;
const CloseButton = styled.button`
  width: 100%;
  padding: 12px 16px;
  border: none;
  border-radius: 8px;
  font-family: var(--ff-primary);
  font-weight: 700;
  font-size: 16px;
  color: var(--c-primary);
  background: var(--bg-primary);
  cursor: pointer;
`;

export default ErrorBoundary;
